import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Filter, Search } from "lucide-react";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { StatusBadge, PriorityBadge, DeadlineBadge } from "../shared/Badge";
import { UserAvatar } from "../shared/UserAvatar";
import { EmptyState } from "../shared/EmptyState";
import { PageLoader } from "../shared/LoadingSpinner";
import {
    formatRelativeTime,
    getTicketTypeConfig,
    TICKET_TYPES,
    STATUSES,
    PRIORITIES,
} from "../../lib/utils";

export function TicketList() {
    const { currentUser, getUserById, hasPermission, wsClient, isTeamSupervisor, supervisedDeptIds } = useApp();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    const isAgentOrAdmin = hasPermission("agent");
    const isCoordinatorOrAbove = hasPermission("coordinator");

    // Team toggle: "mine" | "team"
    const [teamView, setTeamView] = useState("mine");

    // Filters
    const statusFilter = searchParams.get("status") || "";
    const typeFilter = searchParams.get("type") || "";
    const priorityFilter = searchParams.get("priority") || "";

    // Define loadTickets BEFORE useEffects that depend on it (avoid TDZ error)
    const loadTickets = useCallback(async () => {
        if (!currentUser) return;
        try {
            setLoading(true);
            const params = {};
            // Coordinators, agents & admins see all tickets; users see only their own
            if (!isCoordinatorOrAbove) {
                params.involved_user_id = currentUser.id;
                // Team supervisor viewing team tickets: add supervised dept IDs
                if (isTeamSupervisor && teamView === "team") {
                    params.supervised_dept_ids = supervisedDeptIds.join(",");
                }
            }
            if (statusFilter) params.status = statusFilter;
            if (typeFilter) params.type = typeFilter;
            if (priorityFilter) params.priority = priorityFilter;

            const data = await api.getTickets(params);

            // Client-side filter for "mine" vs "team" when supervisor
            if (!isCoordinatorOrAbove && isTeamSupervisor && teamView === "mine") {
                // Even though supervised_dept_ids isn't sent, involved_user_id already filters to user's own
                setTickets(data);
            } else if (!isCoordinatorOrAbove && isTeamSupervisor && teamView === "team") {
                // Show only team tickets (exclude user's own that aren't in supervised depts)
                setTickets(data);
            } else {
                setTickets(data);
            }
        } catch (error) {
            console.error("Failed to load tickets:", error);
        } finally {
            setLoading(false);
        }
    }, [currentUser, isCoordinatorOrAbove, isTeamSupervisor, teamView, supervisedDeptIds, statusFilter, typeFilter, priorityFilter]);

    useEffect(() => {
        loadTickets();
    }, [loadTickets]);

    // 10B — WebSocket: auto-refresh ticket list on events
    useEffect(() => {
        if (!wsClient) return;
        const refresh = () => loadTickets();
        const unsub1 = wsClient.on("ticket_created", refresh);
        const unsub2 = wsClient.on("ticket_updated", refresh);
        return () => { unsub1(); unsub2(); };
    }, [wsClient, loadTickets]);

    const updateFilter = (key, value) => {
        const newParams = new URLSearchParams(searchParams);
        if (value && value !== "all") {
            newParams.set(key, value);
        } else {
            newParams.delete(key);
        }
        setSearchParams(newParams);
    };

    const filteredTickets = tickets.filter((ticket) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            ticket.title.toLowerCase().includes(query) ||
            ticket.ticket_number.toLowerCase().includes(query) ||
            ticket.description?.toLowerCase().includes(query)
        );
    });

    return (
        <div className="page-container" data-testid="ticket-list">
            {/* Header */}
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">{!isCoordinatorOrAbove && isTeamSupervisor && teamView === "team" ? "Tickets de mon équipe" : "Mes tickets"}</h1>
                    <p className="page-subtitle">
                        {tickets.length} ticket{tickets.length !== 1 ? "s" : ""}
                    </p>
                </div>
                <Link to="/nouveau">
                    <Button data-testid="new-ticket-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Nouveau ticket
                    </Button>
                </Link>
            </div>

            {/* Team toggle tabs */}
            {!isCoordinatorOrAbove && isTeamSupervisor && (
                <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg w-fit" data-testid="team-toggle">
                    <button
                        onClick={() => setTeamView("mine")}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${teamView === "mine" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                        data-testid="tab-my-tickets"
                    >
                        Mes demandes
                    </button>
                    <button
                        onClick={() => setTeamView("team")}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${teamView === "team" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                        data-testid="tab-team-tickets"
                    >
                        {"Mon équipe"}
                    </button>
                </div>
            )}

            {/* Filters */}
            <div className="card mb-6">
                <div className="card-body">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex-1 min-w-[200px] max-w-md">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input
                                    placeholder="Rechercher..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9"
                                    data-testid="search-input"
                                />
                            </div>
                        </div>
                        <Select
                            value={statusFilter || "all"}
                            onValueChange={(v) => updateFilter("status", v)}
                        >
                            <SelectTrigger className="w-[180px]" data-testid="filter-status">
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                {STATUSES.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={typeFilter || "all"}
                            onValueChange={(v) => updateFilter("type", v)}
                        >
                            <SelectTrigger className="w-[180px]" data-testid="filter-type">
                                <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les types</SelectItem>
                                {TICKET_TYPES.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>
                                        {t.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={priorityFilter || "all"}
                            onValueChange={(v) => updateFilter("priority", v)}
                        >
                            <SelectTrigger className="w-[180px]" data-testid="filter-priority">
                                <SelectValue placeholder="Priorité" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes priorités</SelectItem>
                                {PRIORITIES.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                        {p.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Ticket List */}
            {loading ? (
                <PageLoader />
            ) : filteredTickets.length === 0 ? (
                <div className="card">
                    <EmptyState
                        title="Aucun ticket"
                        description={
                            searchQuery || statusFilter || typeFilter || priorityFilter
                                ? "Aucun ticket ne correspond à vos critères"
                                : "Vous n'avez pas encore créé de ticket"
                        }
                        action={
                            <Link to="/nouveau">
                                <Button>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Créer un ticket
                                </Button>
                            </Link>
                        }
                    />
                </div>
            ) : (
                <div className="card">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Ticket</th>
                                    <th>Type</th>
                                    <th>Statut</th>
                                    <th>Priorité</th>
                                    <th>Assigné</th>
                                    <th>Créé</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTickets.map((ticket) => {
                                    const typeConfig = getTicketTypeConfig(ticket.type);
                                    const assignee = getUserById(ticket.assigned_to_id);
                                    const benUser = ticket.beneficiary_id && ticket.beneficiary_id !== ticket.requester_id
                                        ? getUserById(ticket.beneficiary_id) : null;
                                    return (
                                        <tr
                                            key={ticket.id}
                                            className="cursor-pointer hover:bg-gray-50"
                                            onClick={() => navigate(`/tickets/${ticket.id}`)}
                                            data-testid={`ticket-row-${ticket.id}`}
                                        >
                                            <td>
                                                <div className="flex flex-col">
                                                    <span className="text-xs font-mono text-gray-400">
                                                        {ticket.ticket_number}
                                                    </span>
                                                    <span className="font-medium text-gray-900">
                                                        {ticket.title}
                                                    </span>
                                                    {benUser && (
                                                        <span className="text-xs text-green-600" data-testid={`ticket-beneficiary-${ticket.id}`}>
                                                            pour {benUser.first_name} {benUser.last_name}
                                                            {benUser.user_type === "external" && <span className="inline-flex items-center ml-1 px-1 py-0 rounded text-[9px] font-semibold bg-amber-100 text-amber-700">EXT</span>}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="text-sm text-gray-600">
                                                    {typeConfig.label}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <StatusBadge status={ticket.status} />
                                                    <DeadlineBadge info={ticket.deadline_info} />
                                                </div>
                                            </td>
                                            <td>
                                                <PriorityBadge priority={ticket.priority} />
                                            </td>
                                            <td>
                                                {assignee ? (
                                                    <div className="flex items-center gap-2">
                                                        <UserAvatar user={assignee} size="sm" />
                                                        <span className="text-sm">
                                                            {assignee.first_name}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 text-sm">
                                                        Non assigné
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className="text-sm text-gray-500">
                                                    {formatRelativeTime(ticket.created_at)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
